/**
 * [글자색·형광펜]
 * - 역할: TipTap Color/BackgroundColor 프리셋 + 최근 사용(localStorage)
 * - 작성/수정일: 2026-08-12
 * - 수정일: 2026-08-12 — Notion 팔레트 톤 정렬
 */

export const G5_COLOR_RECENT_KEY = 'g5-gurumi-recent-colors';
export const G5_COLOR_RECENT_MAX = 8;

/** Text Color — Notion 계열 10색 (null = 기본) */
export const TEXT_COLOR_PRESETS = [
  { label: '기본', value: null, swatch: '#37352F' },
  { label: '회색', value: '#787774', swatch: '#787774' },
  { label: '갈색', value: '#9F6B53', swatch: '#9F6B53' },
  { label: '주황', value: '#D9730D', swatch: '#D9730D' },
  { label: '노랑', value: '#CB912F', swatch: '#CB912F' },
  { label: '초록', value: '#448361', swatch: '#448361' },
  { label: '파랑', value: '#337EA9', swatch: '#337EA9' },
  { label: '보라', value: '#9065B0', swatch: '#9065B0' },
  { label: '분홍', value: '#C14C8A', swatch: '#C14C8A' },
  { label: '빨강', value: '#E03E3E', swatch: '#E03E3E' },
];

/** Highlight Color — Notion 파스텔 10색 (null = 기본) */
export const HIGHLIGHT_COLOR_PRESETS = [
  { label: '기본', value: null, swatch: '#FFFFFF' },
  { label: '회색', value: '#F1F1EF', swatch: '#F1F1EF' },
  { label: '갈색', value: '#F4EEEE', swatch: '#F4EEEE' },
  { label: '주황', value: '#FCECD9', swatch: '#FCECD9' },
  { label: '노랑', value: '#FAF3DD', swatch: '#FAF3DD' },
  { label: '초록', value: '#EEF3ED', swatch: '#EEF3ED' },
  { label: '파랑', value: '#E9F3F7', swatch: '#E9F3F7' },
  { label: '보라', value: '#F6F3F8', swatch: '#F6F3F8' },
  { label: '분홍', value: '#F9F2F5', swatch: '#F9F2F5' },
  { label: '빨강', value: '#FAECEC', swatch: '#FAECEC' },
];

/**
 * [글자색·형광펜]
 * - 역할: 최근 사용 목록 로드
 * - 작성/수정일: 2026-08-12
 * @returns {{ kind: 'text'|'highlight', value: string }[]}
 */
export function loadRecentColors() {
  try {
    const raw = localStorage.getItem(G5_COLOR_RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && (item.kind === 'text' || item.kind === 'highlight') && typeof item.value === 'string' && item.value)
      .slice(0, G5_COLOR_RECENT_MAX);
  } catch (_err) {
    return [];
  }
}

/**
 * [글자색·형광펜]
 * - 역할: 최근 사용에 색 추가
 * - 작성/수정일: 2026-08-12
 */
export function pushRecentColor(kind, value) {
  if (!value || (kind !== 'text' && kind !== 'highlight')) return;
  const next = [{ kind, value }, ...loadRecentColors().filter((item) => !(item.kind === kind && item.value === value))];
  try {
    localStorage.setItem(G5_COLOR_RECENT_KEY, JSON.stringify(next.slice(0, G5_COLOR_RECENT_MAX)));
  } catch (_err) {
    /* ignore quota */
  }
}

/**
 * [글자색·형광펜]
 * - 역할: 선택 구간에 글자색 적용/제거
 * - 작성/수정일: 2026-08-12
 */
export function applyTextColor(editor, value) {
  if (!editor) return;
  if (!value) {
    editor.chain().focus().unsetColor().run();
    return;
  }
  editor.chain().focus().setColor(value).run();
  pushRecentColor('text', value);
}

/**
 * [글자색·형광펜]
 * - 역할: 선택 구간에 형광펜 적용/제거
 * - 작성/수정일: 2026-08-12
 */
export function applyHighlightColor(editor, value) {
  if (!editor) return;
  if (!value) {
    editor.chain().focus().unsetBackgroundColor().run();
    return;
  }
  editor.chain().focus().setBackgroundColor(value).run();
  pushRecentColor('highlight', value);
}

/**
 * [글자색·형광펜]
 * - 역할: 현재 선택 글자색/형광펜 조회
 * - 작성/수정일: 2026-08-12
 */
export function getActiveColors(editor) {
  if (!editor) return { text: null, highlight: null };
  const textStyle = editor.getAttributes('textStyle') || {};
  return {
    text: textStyle.color || null,
    highlight: textStyle.backgroundColor || null,
  };
}
